import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';
import { Skeleton, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { Clock } from '@phosphor-icons/react';

import { AvatarDesignStyled, NoAvatarIconStyled } from './EOICardStyled';
import { DependentDto as DependentEnrollmentDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { DisplayNoAvatarIcon } from '../../Shared/DisplayProfilePicture/DisplayAvatarIconList';
import {
  useGroupDataByCarrier,
  getProfileById,
  navToPluginEOI,
  getAvatarDetails,
  GroupedEoiData,
  EoiDataDto,
  collectCoveragesPendingEoi,
} from './helper/EOICardHelper';
import { DependentDto } from 'enrollment/features/MyFamily/types';
import { PsButtonTuner, PsModalTuner } from 'stencilSetup';
import {
  BenefitsResponseDto,
  CoverageDto,
  useBenefitsQuery,
  useEnrollmentContextQuery,
} from 'theSource/queries';
import {
  PopupContentResponseDto,
  PopupContentRequestDto,
  PopupContentDto,
} from 'enrollment/features/ToDoList/types';
import {
  ENROLLMENT_CONTEXT_NONE,
  POPUP_CONTENT_QUERY_KEY,
} from 'enrollment/features/ToDoList/utils';
import { ErrorResponseDto } from 'theSource/types';
import { useApiMutation } from 'theSource/utils';
import { selectIsAdmin } from 'theSource/features';
import { formatDate } from 'enrollment/features/Plans/PlanCard/utils';
import {
  openPlanPopup,
  PlanPopup,
} from 'enrollment/features/Plans/PlanCard/components';

export interface SSOCardProps {
  lumosApiHost?: string;
}

export const EOICards = ({ lumosApiHost }: SSOCardProps) => {
  const { t } = useTranslation('pages.eoi');
  const isMobile = useMediaQuery('(max-width: 744px)');

  const truncateName = (name: string) => {
    return name.length > 18 ? `${name.substring(0, 17)}...` : name;
  };

  const [popupContent, setPopupContentData] = useState<PopupContentDto | null>(
    null
  );
  const [popupIndex, setPopupIndex] = useState(-1); // planId (unique for each plan year)
  const enrollment = useSelector((state: any) => state?.enrollment);
  const isAdmin = useSelector(selectIsAdmin);

  const subscriber = enrollment?.subscriber;
  const subscriberName = `${subscriber?.first_name} ${subscriber?.last_name}`;
  const profilePicture = subscriber?.subscriber_profile?.profile_picture;
  const avatar = subscriber?.subscriber_profile?.avatar;
  const dependents = enrollment?.dependents?.dependents as DependentDto[];
  const lifeEventCompleted =
    enrollment?.lifeEventCompleted?.lifeEventCompleted || false;

  const { data: benefitsData, isLoading } = useBenefitsQuery({ enabled: true });

  const coveragesPendingEoi = collectCoveragesPendingEoi(benefitsData?.data);
  const groupedData = useGroupDataByCarrier(
    benefitsData?.data || ({} as BenefitsResponseDto),
    coveragesPendingEoi
  );

   const getUniqueCarriersByPlanYear = (planYearData: any) => {
    const seen = new Set();
    return Object.keys(planYearData).filter((carrier) => {
      const key = JSON.stringify(planYearData[carrier].eoiData);
      if (seen.has(key)){ return false; };
      seen.add(key);
      return true;
    });
  };

  // grouped by carrier and plan year (current and next)
  const memoizedGroupedData = useMemo(() => groupedData, [groupedData]);
  
  const groupedCarrierLogos = useMemo(() => {
    return {
      current: [] as string[],
      next: [] as string[],
    };
  }, []);
  Object.keys(memoizedGroupedData).forEach((pyEOI) => {
    const memoKey = pyEOI as keyof typeof memoizedGroupedData;
    Object.keys(memoizedGroupedData[memoKey]).forEach((carrier) => {
      groupedCarrierLogos[memoKey].push(
        memoizedGroupedData[memoKey][carrier].carrierLogoUrl
      );
    });
  });
  const eoiMultiplePY =
    Object.keys(memoizedGroupedData.next).length > 0 &&
    Object.keys(memoizedGroupedData.current).length > 0;

  const { enrollmentContext: contexts } = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });

  // api for popup content not used for plugin
  const { isLoading: isLoadingPopup, mutate: getPopupContent } = useApiMutation<
    PopupContentResponseDto,
    ErrorResponseDto,
    PopupContentRequestDto
  >({
    queryKey: POPUP_CONTENT_QUERY_KEY,
    method: 'post',
    endpoint: '/cart/coverages/popups',
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
    onSuccess: (data) => {
      setPopupContentData(data.data);
    },
  });

  const mapElectionData = (coverage?: CoverageDto) => {
    return {
      increment: coverage?.req_increments || '',
      line_increments: '',
      coverage_level_id: coverage?.coverage_level_id || 0,
      org_plan_id: coverage?.org_plan_id || 0,
      dependent_ids:
        coverage?.req_dependents.map((dep) => dep.dependent_id) || [],
      subscriber_amount: coverage?.subscriber_premium || 0,
    };
  };

  const handleClick = (
    popup: boolean,
    orgBenefitId: number,
    coverage: CoverageDto,
    disabled: boolean,
    planId: number
  ) => {
    if (disabled) {
      return;
    }

    if (popup) {
      const context = contexts?.data.find(
        (context) => context.org_plan_year_id === coverage.org_plan_year_id
      );
      const contextTypeLookup =
        context?.enrollment_type_lookup_code || ENROLLMENT_CONTEXT_NONE;

      getPopupContent({
        enrollment_context_type: contextTypeLookup,
        life_event_completed: lifeEventCompleted,
        org_benefit_id: orgBenefitId,
        election: mapElectionData(coverage),
      });
      setPopupIndex(planId);
    } else {
      return;
    }
  };

  const pluginModalContent = (item: GroupedEoiData) => {
    return (
      <div>
        <div className="d-flex align-items-center pb-4">
          <img
            src={item.carrierLogoUrl}
            alt={item.carrierName}
            data-testid="carrier-logo"
          />
          <div className="float-right d-flex w-75 justify-content-end">
            <Clock size={24} />
            <p className="mb-0 pl-2">{t('timeCounter')}</p>
          </div>
        </div>
        <div>
          <p>{t('pluginModal1')}</p>
          <p>{t('pluginModal2')}</p>
        </div>
      </div>
    );
  };

  const renderProfile = (profile: any, dependent: DependentEnrollmentDto) => {
    if (profile === null) {
      return (
        <NoAvatarIconStyled>
          <DisplayNoAvatarIcon />
        </NoAvatarIconStyled>
      );
    } else if (typeof profile === 'string') {
      return (
        <img
          src={profile}
          alt={t('dependentCovered', {
            dependent: dependent.dependent_name,
          })}
        />
      );
    } else {
      return <AvatarDesignStyled>{profile}</AvatarDesignStyled>;
    }
  };

  const renderBoxNew = (
    item: EoiDataDto,
    indexCarrier: number,
    indexItem: number,
    carrier: string,
    pyKey: keyof typeof memoizedGroupedData
  ) => (
    <>
      <div className="mb-4">
        <div className="body2 mb-2 d-flex align-items-center">
          <div className="flex-fill">
            {item.planNames}:
          </div>
          {!isMobile && (
            <PsButtonTuner
              data-testid="open-new-tab"
              buttonId={`open_new_tab_${indexCarrier}-${indexItem}`}
              label={t('openNewTab')}
              level="tertiary"
              iconRight="arrow-right"
              extraClass="btn btn-link"
              disabled={
                item.disabled ||
                isLoadingPopup ||
                (item.popup ? false : isAdmin) //disable button if admin and is a plugin EOI
              }
              {...(Boolean(item.popup)
                ? {
                    onClick: () =>
                      handleClick(
                        Boolean(item.popup),
                        item.orgBenefitId,
                        item.coverage,
                        item.disabled || isLoadingPopup,
                        item.coverage.org_plan_id
                      ),
                  }
                : {
                    'data-bs-toggle': isAdmin ? '' : 'modal',
                    'data-bs-target': isAdmin
                      ? ''
                      : `#eoi-modal-${indexCarrier}-${indexItem}`,
                  })}
            />
          )}
        </div>
        <div className="row mb-3">
          {item.dependentCoverages.length > 0 &&
            item.dependentCoverages.map((dependent) => {
              const profile = getProfileById(
                dependent.dependent_id,
                dependents
              );
              return (
                <div className="col-12 col-md-4 col-lg-3 mb-2 body2 d-flex align-items-center" key={dependent.dependent_id}>
                  {renderProfile(profile, dependent)}
                  <div className="ml-2">
                    {isMobile ? dependent.dependent_name : truncateName(dependent.dependent_name)}
                  </div>
                </div>
              );
            }
          )}
          {((item.popup && item.dependentCoverages.length === 0) ||
            !item.popup) && (
              <div className="col-12 col-md-4 col-lg-3 mb-2 body2 d-flex align-items-center">
                {profilePicture ? (
                    <img src={profilePicture} alt={t('youAreCovered')} />
                  ) : avatar ? (
                    <AvatarDesignStyled>
                      {getAvatarDetails(avatar)}
                    </AvatarDesignStyled>
                  ) : (
                    <NoAvatarIconStyled>
                      <DisplayNoAvatarIcon />
                    </NoAvatarIconStyled>
                  )}
                <div className="ml-2">
                  {subscriberName}
                </div>
              </div>
            )
          }
        </div>
      </div>
      {/* item.popup */}
      {item.popup ? (
        item.coverage.org_plan_id &&
        lumosApiHost && (
          // for popup EOI
          <PlanPopup
            planId={item.coverage.org_plan_id}
            popupContent={popupContent ? popupContent : undefined}
            apiHost={lumosApiHost}
            onClose={() => setPopupIndex(-1)}
          />
        )
      ) : (
        // for plugin EOI
        <PsModalTuner
          modalId={`eoi-modal-${indexCarrier}-${indexItem}`}
          data-testid="eoi-modal"
          modalTitle={t('pages.home:answerEoi')}
          primaryActive
          primaryButton={t('submitModalBtn')}
          secondaryButton={t('common:cancel')}
          onPsModalSubmit={() =>
            navToPluginEOI(
              memoizedGroupedData[pyKey][carrier],
              lifeEventCompleted,
              contexts,
              lumosApiHost || ''
            )
          }
        >
          {pluginModalContent(memoizedGroupedData[pyKey][carrier])}
        </PsModalTuner>
      )}
      {/* isMobile */}
      {isMobile && (
        <div className="d-flex justify-content-center pb-3">
          <PsButtonTuner
            data-testid="open-new-tab"
            buttonId={`open_new_tab_${indexCarrier}_${indexItem}`}
            label={t('openNewTabMobile')}
            level="tertiary"
            iconRight="arrow-right"
            extraClass="btn btn-link"
            disabled={
              item.disabled || isLoadingPopup || (item.popup ? false : isAdmin)
            } //disable button if admin and is a plugin EOI
            {...(Boolean(item.popup)
              ? {
                  onClick: () =>
                    handleClick(
                      Boolean(item.popup),
                      item.orgBenefitId,
                      item.coverage,
                      item.disabled || isLoadingPopup,
                      item.coverage.org_plan_id
                    ),
                }
              : {
                  'data-bs-toggle': isAdmin ? '' : 'modal',
                  'data-bs-target': isAdmin
                    ? ''
                    : `#eoi-modal-${indexCarrier}-${indexItem}`,
                })}
          />
        </div>
      )}
    </>
  );

  useEffect(() => {
    // open the popup if it is a popup EOI and the popupContent is available (wait for the api call to finish)
    if (popupContent && popupIndex >= 0 && !isLoadingPopup) {
      openPlanPopup(popupIndex);
    }
  }, [popupContent, popupIndex, isLoadingPopup]);

  if (!benefitsData && isLoading) {
    // usually skeleton will be bypassed since benefitsData is cached from the homepage
    return (
      <Skeleton
        variant="rectangular"
        height={200}
        role="status"
        aria-label="Loading..."
      />
    );
  } else if (!benefitsData && !isLoading) {
    return null;
  }

  return Object.keys(memoizedGroupedData).map((pyKey, index) => {
    const planYearKey = pyKey as keyof typeof memoizedGroupedData;
    const startsOn = coveragesPendingEoi[planYearKey][0]?.plan_year_start_date;
    const endsOn = coveragesPendingEoi[planYearKey][0]?.plan_year_end_date;

    return (
      <div
        className={`pb-4 ${eoiMultiplePY && index === 0 && 'border-bottom'}`}
        key={planYearKey}
      >
        {eoiMultiplePY && (
          <p className="mb-0 pt-4">
            {t('planYear')}: {formatDate(startsOn)} - {formatDate(endsOn)}
          </p>
        )}
        {getUniqueCarriersByPlanYear(memoizedGroupedData[planYearKey]).map(
          (carrier, indexCarrier) => {
            const carrierData = memoizedGroupedData[planYearKey][carrier];
            
            return (
              <div className="card pt-4_5" data-testid="sso-card" key={carrier}>
                {/* Logo */}
                {carrierData.carrierLogoUrl.length > 0 && (
                  <img
                    src={`${carrierData.carrierLogoUrl}`}
                    alt={carrier}
                    data-testid="carrier-logo"
                  />
                )}

                <hr className="mx-0" />

                {/* Plan Name */}
                <div className="card-body">
                  <h3 className="body1 semiBold">{carrier} {t('EOI')}</h3>
                </div>

                {/* Answer a few... paragraph */}
                <p className="body1 mb-4">{t('answerQuestions')}</p>

                {/* Elected Benefits */}
                <h4 className="semiBold">
                  {t('electedBenefits')}
                </h4>
                {memoizedGroupedData[planYearKey][carrier].eoiData.map(
                  (item, indexItem) => {
                    return (
                      <>
                        {renderBoxNew(
                          item,
                          indexCarrier,
                          indexItem,
                          carrier,
                          planYearKey
                        )}
                      </>
                    );
                  }
                )}

              </div>
            );
          }
        )}
      </div>
    );
  });
};
